
import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';
import { User, CreateUser, UpdateUser, InviteUser, InviteUserErrorBody, UserRole } from '@/types';

export type InviteUserResult =
  | { success: true; message: string }
  | { success: false; error: string; inviteLink?: string };

/** Response shape from GET /admin/users (paginated). */
interface PaginatedUsersResponse {
  items: User[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface UseUsersReturn {
  users: User[];
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  createUser: (data: CreateUser) => Promise<User | null>;
  inviteUser: (data: InviteUser) => Promise<InviteUserResult>;
  updateUser: (id: string, data: UpdateUser) => Promise<User | null>;
  deactivateUser: (id: string) => Promise<boolean>;
  activateUser: (id: string) => Promise<boolean>;
  deleteUser: (id: string) => Promise<boolean>;
  resetPassword: (id: string, newPassword: string) => Promise<boolean>;
}

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<PaginatedUsersResponse>('/admin/users');
      if (response.error) {
        setError(response.error);
      } else if (response.data?.items) {
        setUsers(response.data.items);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createUser = useCallback(
    async (data: CreateUser): Promise<User | null> => {
      setError(null);
      try {
        const response = await api.post<User>('/admin/users', data);
        if (response.error) {
          setError(response.error);
          return null;
        }
        if (response.data) {
          setUsers((prev) => [...prev, response.data!]);
          return response.data;
        }
        return null;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create user');
        return null;
      }
    },
    []
  );

  const inviteUser = useCallback(
    async (data: InviteUser): Promise<InviteUserResult> => {
      setError(null);
      try {
        const response = await api.post<{ message: string } | InviteUserErrorBody>(
          '/admin/users/invite',
          data
        );
        if (response.error) {
          const body = response.data as InviteUserErrorBody | null;
          const inviteLink = body?.inviteLink ?? (body as { invite_link?: string })?.invite_link;
          if (inviteLink) {
            await fetchUsers();
          }
          return {
            success: false,
            error: response.error,
            inviteLink,
          };
        }
        if (response.data && 'message' in response.data) {
          await fetchUsers();
          return { success: true, message: response.data.message };
        }
        return { success: false, error: 'Unknown response' };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send invite';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [fetchUsers]
  );

  const updateUser = useCallback(
    async (id: string, data: UpdateUser): Promise<User | null> => {
      setError(null);
      try {
        const response = await api.put<User>(`/admin/users/${id}`, data);
        if (response.error) {
          setError(response.error);
          return null;
        }
        if (response.data) {
          setUsers((prev) =>
            prev.map((user) => (user.id === id ? response.data! : user))
          );
          return response.data;
        }
        return null;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update user');
        return null;
      }
    },
    []
  );

  const deactivateUser = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      const response = await api.delete<{ message: string }>(
        `/admin/users/${id}`
      );
      if (response.error) {
        setError(response.error);
        return false;
      }
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, isActive: false } : user
        )
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
      return false;
    }
  }, []);

  const activateUser = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      const response = await api.put<User>(`/admin/users/${id}`, {
        isActive: true,
      });
      if (response.error) {
        setError(response.error);
        return false;
      }
      if (response.data) {
        setUsers((prev) =>
          prev.map((user) => (user.id === id ? response.data! : user))
        );
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate user');
      return false;
    }
  }, []);

  const deleteUser = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      const response = await api.delete<{ message: string }>(
        `/admin/users/${id}/permanent`
      );
      if (response.error) {
        setError(response.error);
        return false;
      }
      setUsers((prev) => prev.filter((user) => user.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      return false;
    }
  }, []);

  const resetPassword = useCallback(
    async (id: string, newPassword: string): Promise<boolean> => {
      setError(null);
      try {
        const response = await api.put<User>(`/admin/users/${id}`, {
          password: newPassword,
        });
        if (response.error) {
          setError(response.error);
          return false;
        }
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to reset password');
        return false;
      }
    },
    []
  );

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    createUser,
    inviteUser,
    updateUser,
    deactivateUser,
    activateUser,
    deleteUser,
    resetPassword,
  };
}
